import React, { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Briefcase, Check, FileText, Shield, Trophy, Upload, Zap } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import TrustStrip from '../components/TrustStrip';
import { API_BASE as API_URL } from '../utils/apiBase';

// FAQ schema — lives ONLY on this page to avoid duplicate FAQPage flags in Google Search Console.
const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is an ATS resume checker?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "An ATS (Applicant Tracking System) resume checker analyzes your resume against the same algorithms that employers use to filter job applications. It scores your resume's compatibility, identifies missing keywords, and suggests improvements to help you pass automated screening and land more interviews.",
      },
    },
    {
      '@type': 'Question',
      name: 'How do I check my resume ATS score for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "You can check your ATS score for free at ascendforyou.net/free-ats-check. Simply paste your resume text, enter the job title you're targeting, and get an instant score with specific strengths, critical gaps, and quick fixes. No account or credit card required.",
      },
    },
    {
      '@type': 'Question',
      name: 'Why is my resume getting rejected by ATS systems?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Common reasons include: missing keywords from the job description, using fancy formatting that ATS can't parse (tables, columns, graphics), creative section headers instead of standard ones (Experience, Education, Skills), and submitting in unsupported file formats. About 75% of qualified candidates get filtered out before a human sees their resume.",
      },
    },
    {
      '@type': 'Question',
      name: 'How can I optimize my resume for ATS?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Use simple formatting with a single column layout, mirror exact keywords from the job description, use standard section headers, include quantifiable achievements with numbers and percentages, and submit as .docx format. AI tools like Ascend can analyze your resume and provide specific keyword recommendations for your target role.',
      },
    },
    {
      '@type': 'Question',
      name: 'What percentage of companies use ATS to screen resumes?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Over 90% of Fortune 500 companies and approximately 75% of all employers use ATS (Applicant Tracking Systems) to screen resumes. This means the majority of job applications are filtered by software before a human recruiter ever reads them.',
      },
    },
  ],
};

const SAMPLE_TARGET_JOB = 'Marketing Manager';
const SAMPLE_RESUME_TEXT = `Sarah Chen
Marketing Manager | Digital Strategy & Growth

EXPERIENCE

Marketing Manager — TechFlow Inc. (2022–Present)
• Led cross-channel campaigns across paid social, SEM, and email that drove 2.3M impressions and 45K qualified leads in 12 months
• Managed $180K monthly ad budget across Google Ads, Meta, and LinkedIn with a blended ROAS of 4.2x
• Built and optimized a 12-email nurture sequence that improved MQL-to-SQL conversion by 34%
• Partnered with product and sales to launch 3 product lines, each exceeding first-quarter revenue targets by 20%+

Digital Marketing Specialist — BrightPath Media (2019–2022)
• Created and A/B tested landing pages that increased conversion rates from 2.1% to 5.8%
• Grew organic blog traffic from 8K to 47K monthly sessions through SEO content strategy
• Managed social media calendar across 4 platforms, growing combined following by 12K in 18 months

Marketing Coordinator — Starter Agency (2017–2019)
• Coordinated 15+ client campaigns simultaneously, maintaining 98% on-time delivery rate
• Produced weekly analytics reports tracking KPIs across web, social, and email channels

EDUCATION
B.S. Marketing, University of Michigan — 2017

SKILLS
Google Ads, Meta Ads Manager, HubSpot, Salesforce, Google Analytics 4, SEMrush, Mailchimp, Figma, SQL, A/B Testing, Marketing Automation`;

const ChallengerBanner = ({ challenger, beat }) => (
  <div className="max-w-3xl mx-auto mb-6 mt-4 px-4 sm:px-6" data-testid="challenger-banner">
    <div className="bg-gradient-to-r from-[#002fa7] to-[#001d6a] text-white p-5 flex items-center gap-4">
      <Trophy size={28} className="flex-shrink-0 opacity-90" />
      <div className="flex-1">
        <p className="font-satoshi text-lg font-bold leading-tight">{challenger} scored {beat}/100. Can you beat it?</p>
        <p className="text-white/80 text-sm mt-0.5">Paste your resume below and find out in 30 seconds — free.</p>
      </div>
    </div>
  </div>
);

// ZERO-JS ANCHOR FLOW (V4): the form below is a NATIVE HTML form. Submission
// is a standard browser POST + full page navigation to the backend, which
// responds with a complete server-rendered HTML results page. No fetch, no
// axios, no preventDefault, no client-side state transition.
export default function FreeATSCheck() {
  const [searchParams] = useSearchParams();
  const challenger = searchParams.get('challenger');
  const beat = searchParams.get('beat');
  const preRole = searchParams.get('role');
  // Ad attribution: forwarded through the native form → results page →
  // email capture, so paid leads are tagged in the admin pipeline.
  const utmSource = searchParams.get('utm_source') || (searchParams.get('fbclid') ? 'facebook' : '');
  const utmCampaign = searchParams.get('utm_campaign') || '';

  const targetJobRef = useRef(null);
  const resumeTextRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadState, setUploadState] = useState({ status: 'idle', fileName: '', error: '' });

  const fillSample = () => {
    if (targetJobRef.current) targetJobRef.current.value = SAMPLE_TARGET_JOB;
    if (resumeTextRef.current) resumeTextRef.current.value = SAMPLE_RESUME_TEXT;
  };

  // Optional convenience: parse a PDF/DOCX into the textarea BEFORE submit.
  // This never touches the submission path — the form POST stays native.
  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUploadState({ status: 'error', fileName: '', error: 'File too large. Max 5MB.' });
      return;
    }
    setUploadState({ status: 'uploading', fileName: file.name, error: '' });
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_URL}/api/free-ats-check/parse-file`, { method: 'POST', body: form });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || 'Could not read this file.');
      }
      const data = await res.json();
      if (resumeTextRef.current) resumeTextRef.current.value = data.text || '';
      setUploadState({ status: 'done', fileName: file.name, error: '' });
    } catch (err) {
      setUploadState({ status: 'error', fileName: '', error: err.message || 'Upload failed. Paste your resume text instead.' });
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="free-ats-check-page">
      <SEOHead
        title="Free ATS Resume Checker — Instant Score & Fixes | Ascend"
        description="Check your resume's ATS score for free in 30 seconds. Instant keyword matches, critical gaps, and quick fixes. No signup required to see your score."
        canonical="https://ascendforyou.net/free-ats-check"
        jsonLd={FAQ_JSON_LD}
      />
      <section className="pt-16 pb-6 sm:pt-24 sm:pb-12 border-b border-[#e5e7eb]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-2 sm:mb-3 block">
            FREE TOOL
          </span>
          <h1 className="font-satoshi text-2xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-[#0f1115] leading-[1.1]">
            Check Your ATS Score — Free
          </h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#4b5563] max-w-xl mx-auto">
            Paste your resume to get your instant ATS score with actionable feedback. No signup required to see your score.
          </p>
          <div className="mt-4 sm:mt-5 flex flex-col items-center gap-2.5" data-testid="hero-social-proof">
            <TrustStrip />
          </div>
        </div>
      </section>

      <section className="py-6 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {challenger && beat && (
            <ChallengerBanner challenger={challenger} beat={beat} />
          )}

          {/* NATIVE FORM — standard browser POST, full page navigation. */}
          <form
            action={`${API_URL}/api/free-ats-check/submit`}
            method="POST"
            className="space-y-5"
            data-testid="free-ats-form"
          >
            {utmSource && <input type="hidden" name="utm_source" value={utmSource} />}
            {utmCampaign && <input type="hidden" name="utm_campaign" value={utmCampaign} />}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-[#0f1115] mb-2" htmlFor="target_job">
                <Briefcase size={16} className="text-[#002fa7]" />
                Target Job Title *
              </label>
              <input
                ref={targetJobRef}
                id="target_job"
                type="text"
                name="target_job"
                required
                defaultValue={preRole || ''}
                placeholder="e.g., Software Engineer, Marketing Manager"
                className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:outline-none focus:border-[#002fa7]"
                data-testid="target-job-input"
              />
              <button
                type="button"
                onClick={fillSample}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#002fa7] hover:text-[#001f6f] hover:underline"
                data-testid="try-sample-btn"
              >
                <Zap size={12} /> Try with a sample resume to see how it works
              </button>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-[#0f1115] mb-2" htmlFor="resume_text">
                <FileText size={16} className="text-[#002fa7]" />
                Your Resume *
              </label>
              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#0f1115]" data-testid="resume-trust-strip">
                <span className="inline-flex items-center gap-1"><Shield size={11} className="text-[#10b981]" /> Resume never stored</span>
                <span className="inline-flex items-center gap-1"><Check size={11} className="text-[#10b981]" /> No signup</span>
                <span className="inline-flex items-center gap-1"><Zap size={11} className="text-[#10b981]" /> 30-second score</span>
              </div>

              <div className="mb-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="resume-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4b5563] border border-[#e5e7eb] px-3 py-1.5 hover:border-[#002fa7] hover:text-[#002fa7]"
                  data-testid="resume-upload-btn"
                >
                  <Upload size={13} />
                  {uploadState.status === 'uploading' ? 'Reading your resume…'
                    : uploadState.status === 'done' ? `${uploadState.fileName} · click to replace`
                    : 'Upload PDF / Word instead'}
                </button>
                {uploadState.error && (
                  <p className="mt-1 text-xs text-[#e11d48]" data-testid="resume-upload-error">{uploadState.error}</p>
                )}
              </div>

              <textarea
                ref={resumeTextRef}
                id="resume_text"
                name="resume_text"
                required
                minLength={50}
                rows={10}
                placeholder="Paste your full resume text here..."
                className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:outline-none focus:border-[#002fa7] resize-none font-mono"
                data-testid="resume-text-input"
              />
            </div>

            <details>
              <summary className="cursor-pointer text-xs font-semibold text-[#6b7280] hover:text-[#002fa7]" data-testid="show-jd-toggle">
                Have a job description? Add it for better accuracy
              </summary>
              <textarea
                name="job_description"
                rows={4}
                placeholder="Paste the job description for a more targeted analysis..."
                className="mt-2 w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:outline-none focus:border-[#002fa7] resize-none"
                data-testid="job-description-input"
              />
            </details>

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="analyze-btn"
            >
              <Zap size={18} />
              <span>Get My Free ATS Score</span>
            </button>
            <p className="text-center text-xs text-[#9ca3af] flex items-center justify-center gap-1">
              <Shield size={12} /> No signup required to see your score. Your results open on the next page (takes ~20 seconds).
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
